import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Col, Container, Row, Table, Modal, Offcanvas, Dropdown, Badge, Card, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router";
import { FiFilter, FiDownload, FiCalendar, FiUser, FiMapPin, FiToggleLeft, FiToggleRight, FiEye, FiTrash2, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiBook, FiPackage, FiSearch } from 'react-icons/fi';
import { FaBuilding, FaTag } from 'react-icons/fa';
import moment from 'moment';
import Header from "../../Component/Header";
import FullScreenLoader from "../../Component/Loader";
import downArrowIcon from "../../Asset/Img/download-arrow-icon.svg";
import deleteIconModal from "../../Asset/Img/warning-icon.svg";
import warningIcon from "../../Asset/Img/delete-modal-icon.svg";
import calendarIcon from "../../Asset/Img/calendar-icon.svg";
import filterIcon from "../../Asset/Img/filter-icon.svg";
import { getCustomers, getRegions, getPriceBook, updatePriceBookStatus, deletePriceBook } from "../../Services/priceBook";
import SearchableDropdown from "../../Component/Dropdown";
import { ToastContainer, toast } from "react-toastify";
import "./style.css"

// Status Badge Component
const StatusBadge = ({ isActive }) => {
    return (
        <Badge
            pill
            bg={isActive ? 'success' : 'danger'}
            className="px-3 py-2 d-inline-flex align-items-center gap-1"
            style={{ fontSize: '12px', fontWeight: '500' }}
        >
            <span className={`d-inline-block rounded-circle ${isActive ? 'bg-white' : 'bg-white'}`}
                style={{ width: '6px', height: '6px', opacity: 0.8 }} />
            {isActive ? 'Active' : 'Inactive'}
        </Badge>
    );
};

// Custom Pagination Component
const CustomPagination = ({ currentPage, totalPages, setCurrentPage }) => {
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <div className="d-flex align-items-center gap-2">
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
            >
                <FiChevronsLeft />
            </Button>
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
            >
                <FiChevronLeft />
            </Button>

            {getPageNumbers().map((page, index) => (
                page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-muted">…</span>
                ) : (
                    <Button
                        key={page}
                        variant={currentPage === page ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="pagination-btn"
                        style={{ minWidth: '35px' }}
                    >
                        {page}
                    </Button>
                )
            ))}

            <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
            >
                <FiChevronRight />
            </Button>
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
            >
                <FiChevronsRight />
            </Button>
        </div>
    );
};

function PricebookList() {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([])
    const [regionOptions, setRegionOptions] = useState([])
    const [selectedCustomer, setSlectedCustomer] = useState('')
    const [selectedRegion, setSelectedRegion] = useState('')
    const [selectedCountry, setSelectedCountry] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [showLoader, setShowLoader] = useState(false);
    const [priceBookList, setPriceBookList] = useState([])
    const [selectedStatus, setSelectedStatus] = useState({})
    const [deleteData, setDeleteData] = useState('')
    const [filterCount, setFilterCount] = useState(null)
    const [isReset, setIsReset] = useState(false)
    const [isActive, setIsActive] = useState({ label: 'All', value: null })
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalData, setTotalData] = useState(null);
    const [activeData, setActiveData] = useState(null);
    const [inActiveData, setInActiveData] = useState(null);
    const [activeOfs, setActiveOfs] = useState(null);
    const [activeEs, setActiveEs] = useState(null);
    const [activeTab, setActiveTab] = useState('All');
    const [statusOptions, setStatusOption] = useState([
        { label: 'All', value: null },
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' }
    ]);

    // Search filter states
    const [searchFilters, setSearchFilters] = useState({
        sales: '',
        customer: '',
        region: '',
        startDate: '',
        endDate: '',
        status: '',
        version: ''
    });

    const [debouncedSearch, setDebouncedSearch] = useState({
        sales: '',
        customer: '',
        region: '',
        startDate: '',
        endDate: '',
        status: '',
        version: ''
    });

    // Ref to track if debounce timer is active
    const debounceTimerRef = useRef(null);

    // Debounce effect for search filters - 2 seconds delay
    useEffect(() => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer with 2 second delay
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchFilters);
        }, 2000);

        // Cleanup timer on unmount or when searchFilters changes
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchFilters]);

    // Fetch data when debounced search changes
    useEffect(() => {
        if (!isReset) {
            fetchPriceBook();
        }
    }, [currentPage, itemsPerPage, activeTab, debouncedSearch]);

    // Sync activeTab with header status filter
    useEffect(() => {
        const statusValue = searchFilters.status;
        if (statusValue === '') {
            setActiveTab('All');
        } else if (statusValue === 'active') {
            setActiveTab('Active');
        } else if (statusValue === 'inactive') {
            setActiveTab('Inactive');
        }
    }, [searchFilters.status]);

    const totalPages = Math.ceil(totalData / itemsPerPage);

    useEffect(() => {
        fetchCustomers()
        fetchRegions()
    }, []);

    useEffect(() => {
        if (isReset) {
            fetchPriceBook();
            setIsReset(false);
        }
    }, [isReset]);

    let fetchCustomers = async () => {
        let data = await getCustomers(localStorage?.access_token)
        if (data?.data?.status_code == 200) {
            setCustomerOptions(data.data?.customers)
        }
    }

    let fetchRegions = async () => {
        let data = await getRegions(localStorage?.access_token)
        if (data?.data?.status_code == 200) {
            setRegionOptions(data.data?.regions)
        }
    }

    let handleCloseDelete = () => setShowDeleteModal(false)
    let handleCloseStatus = () => setShowStatusModal(false)

    let handleFilterSelect = (value, filter) => {
        if (filter == 'customer') {
            setSlectedCustomer(value)
        } else if (filter == 'status') {
            if (!value) return
            setIsActive(value)
        } else {
            setSelectedRegion(value)
        }
    }

    let countFilledStates = () => {
        let count = 0;
        if (selectedCustomer) count++;
        if (selectedRegion) count++;
        if (startDate) count++;
        if (endDate) count++;
        if (isActive?.value) count++;
        return count;
    }

    let handleApplyFilters = async () => {
        handleClose()
        setCurrentPage(1)
        const filledCount = countFilledStates();
        setFilterCount(filledCount)
        // Update search filters with offcanvas values
        setSearchFilters(prev => ({
            ...prev,
            startDate: startDate,
            endDate: endDate,
            status: isActive?.value === 'true' ? 'active' : isActive?.value === 'false' ? 'inactive' : ''
        }));
        // Update debounced search immediately for date filters
        setDebouncedSearch(prev => ({
            ...prev,
            startDate: startDate,
            endDate: endDate,
            status: isActive?.value === 'true' ? 'active' : isActive?.value === 'false' ? 'inactive' : ''
        }));
        setTimeout(() => {
            fetchPriceBook();
        }, 100);
    }

    let handleReset = async () => {
        handleClose()
        setSlectedCustomer('')
        setSelectedRegion('')
        setStartDate('')
        setEndDate('')
        setIsActive({ label: 'All', value: null })
        setFilterCount(null)
        setCurrentPage(1)
        setActiveTab('All')
        // Reset search filters
        setSearchFilters({
            sales: '',
            customer: '',
            region: '',
            startDate: '',
            endDate: '',
            status: '',
            version: ''
        });
        // Immediately reset debounced search as well
        setDebouncedSearch({
            sales: '',
            customer: '',
            region: '',
            startDate: '',
            endDate: '',
            status: '',
            version: ''
        });
        // Clear any pending debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        setIsReset(true)
    }

    let handleStatusUpdate = (e, val) => {
        setShowStatusModal(true)
        setSelectedStatus({ OrderId: val.OrderId, IsActive: e })
    }

    let handleDeleteClick = (val) => {
        setShowDeleteModal(true)
        setDeleteData(val.OrderId)
    }

    let handleDelete = async () => {
        setShowDeleteModal(false)
        setShowLoader(true)
        let res = await deletePriceBook(deleteData, localStorage.access_token)
        if (res?.data?.status_code == 200) {
            await fetchPriceBook()
            toast.success(res?.data?.message)
        } else if (res?.data?.status_code == 409) {
            toast.warning(res?.data?.message)
            setShowLoader(false)
        } else {
            setShowLoader(false)
        }
        setDeleteData('')
    }

    let updateStatus = async () => {
        setShowStatusModal(false)
        let payload = selectedStatus
        setShowLoader(true)
        let res = await updatePriceBookStatus(payload, localStorage.access_token)
        if (res?.data?.status_code == 200) {
            await fetchPriceBook()
            toast.success(res?.data?.message)
        } else if (res?.data?.status_code == 409) {
            toast.warning(res?.data?.message)
            setShowLoader(false)
        } else {
            setShowLoader(false)
        }
        setSelectedStatus({})
    }

    let handleDateChange = (e, type) => {
        if (type == 'start') {
            setStartDate(e.target.value)
        } else {
            setEndDate(e.target.value)
        }
    }

    // Handle search input change
    const handleSearchChange = (field, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setCurrentPage(1);
    };

    let fetchPriceBook = async () => {
        let filters = {
            region_id: selectedRegion?.RegionId ? selectedRegion.RegionId : '',
            customer_id: selectedCustomer?.CustomerId ? selectedCustomer.CustomerId : '',
            start_date: debouncedSearch.startDate || startDate,
            end_date: debouncedSearch.endDate || endDate,
            page: currentPage,
            page_size: itemsPerPage,
            is_active: activeTab === 'All' ? '' : activeTab === 'Active' ? true : false,
            // Add search filters
            search_sales: debouncedSearch.sales,
            search_customer: debouncedSearch.customer,
            search_region: debouncedSearch.region,
            search_start_date: debouncedSearch.startDate,
            search_end_date: debouncedSearch.endDate,
            search_status: debouncedSearch.status,
            search_version: debouncedSearch.version // Added version filter
        }
        setShowLoader(true)
        let data = await getPriceBook(localStorage?.access_token, filters)
        setShowLoader(false)
        if (data?.data?.status_code == 200) {
            setPriceBookList(data.data?.orders)
            setTotalData(data.data?.total)
            setActiveData(data.data?.Active_pricebook)
            setInActiveData(data.data?.Inactive_pricebook)
            setActiveEs(data.data?.Active_ES_Pricebook)
            setActiveOfs(data.data?.Active_OFS_Pricebook)
        }
    }

    let formatDate = (dateStr) => {
        if (!dateStr) return ""
        return moment(dateStr).format('DD-MM-YYYY');
    }

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        // Update header status filter to match tab
        if (tab === 'All') {
            setSearchFilters(prev => ({ ...prev, status: '' }));
        } else if (tab === 'Active') {
            setSearchFilters(prev => ({ ...prev, status: 'active' }));
        } else if (tab === 'Inactive') {
            setSearchFilters(prev => ({ ...prev, status: 'inactive' }));
        }
    }

    return (
        <div className="wrapper">
            <Header />
            <ToastContainer position="top-right" autoClose={3000} />
            {showLoader ? <FullScreenLoader /> : null}

            <div className="container-layout">
                <Container fluid>
                    {/* Header Section */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
                        <div>
                            <h2 className="main-title-h mb-1">Pricebook List</h2>
                            <p className="text-muted small mb-0">
                                Manage and track all price books across regions and customers
                            </p>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <Row className="mb-4 g-4">
                        <Col xs={12}>
                            <Card className="border-0 shadow-sm stats-card-wrapper">
                                <Card.Body className="p-4">
                                    {/* Tab Filter */}
                                    <div className="d-flex gap-2 mb-4 pb-3 border-bottom stats-tab-container">
                                        <Button
                                            variant={activeTab === 'All' ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleTabChange('All')}
                                            className={`stats-tab-btn ${activeTab === 'All' ? 'active' : ''}`}
                                        >
                                            All
                                            <Badge bg="light" text="dark" className="ms-2 tab-badge">
                                                {totalData || 0}
                                            </Badge>
                                        </Button>
                                        <Button
                                            variant={activeTab === 'Active' ? 'success' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleTabChange('Active')}
                                            className={`stats-tab-btn ${activeTab === 'Active' ? 'active' : ''}`}
                                        >
                                            Active
                                            <Badge bg="light" text="dark" className="ms-2 tab-badge">
                                                {activeData || 0}
                                            </Badge>
                                        </Button>
                                        <Button
                                            variant={activeTab === 'Inactive' ? 'danger' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleTabChange('Inactive')}
                                            className={`stats-tab-btn ${activeTab === 'Inactive' ? 'active' : ''}`}
                                        >
                                            Inactive
                                            <Badge bg="light" text="dark" className="ms-2 tab-badge">
                                                {inActiveData || 0}
                                            </Badge>
                                        </Button>
                                    </div>
                                    {/* Stats Cards - 4 Cards in Sync */}
                                    <Row className="g-4">
                                        <Col xs={12} sm={6} md={3}>
                                            <div className="stats-card-modern stats-card-total">
                                                <div className="stats-card-modern-inner">
                                                    <div className="stats-card-modern-icon-wrapper">
                                                        <div className="stats-card-modern-icon">
                                                            <FaTag size={28} />
                                                        </div>
                                                    </div>
                                                    <div className="stats-card-modern-content">
                                                        <h6 className="stats-card-modern-label">Total Price Books</h6>
                                                        <div className="stats-card-modern-value-wrapper">
                                                            <h3 className="stats-card-modern-value">{totalData || 0}</h3>
                                                        </div>
                                                        <p className="stats-card-modern-sub">Across all regions</p>
                                                    </div>
                                                </div>
                                                <div className="stats-card-modern-progress">
                                                    <div className="stats-card-modern-progress-bar" style={{ width: '100%' }}></div>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={12} sm={6} md={3}>
                                            <div className="stats-card-modern stats-card-active">
                                                <div className="stats-card-modern-inner">
                                                    <div className="stats-card-modern-icon-wrapper">
                                                        <div className="stats-card-modern-icon">
                                                            <FiToggleRight size={28} />
                                                        </div>
                                                    </div>
                                                    <div className="stats-card-modern-content">
                                                        <h6 className="stats-card-modern-label">Active</h6>
                                                        <div className="stats-card-modern-value-wrapper">
                                                            <h3 className="stats-card-modern-value">{activeData || 0}</h3>
                                                        </div>
                                                        <p className="stats-card-modern-sub">Es + Ofs combined</p>
                                                    </div>
                                                </div>
                                                <div className="stats-card-modern-progress">
                                                    <div className="stats-card-modern-progress-bar" style={{
                                                        width: totalData > 0 ? `${(activeData / totalData) * 100}%` : '0%'
                                                    }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={12} sm={6} md={3}>
                                            <div className="stats-card-modern stats-card-es">
                                                <div className="stats-card-modern-inner">
                                                    <div className="stats-card-modern-icon-wrapper">
                                                        <div className="stats-card-modern-icon">
                                                            <FiBook size={28} />
                                                        </div>
                                                    </div>
                                                    <div className="stats-card-modern-content">
                                                        <h6 className="stats-card-modern-label">Active Es</h6>
                                                        <div className="stats-card-modern-value-wrapper">
                                                            <h3 className="stats-card-modern-value">{activeEs || 0}</h3>
                                                        </div>
                                                        <p className="stats-card-modern-sub">Active Es price books</p>
                                                    </div>
                                                </div>
                                                <div className="stats-card-modern-progress">
                                                    <div className="stats-card-modern-progress-bar" style={{
                                                        width: activeData > 0 ? `${(activeEs / activeData) * 100}%` : '0%'
                                                    }}></div>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={12} sm={6} md={3}>
                                            <div className="stats-card-modern stats-card-ofs">
                                                <div className="stats-card-modern-inner">
                                                    <div className="stats-card-modern-icon-wrapper">
                                                        <div className="stats-card-modern-icon">
                                                            <FiPackage size={28} />
                                                        </div>
                                                    </div>
                                                    <div className="stats-card-modern-content">
                                                        <h6 className="stats-card-modern-label">Active Ofs</h6>
                                                        <div className="stats-card-modern-value-wrapper">
                                                            <h3 className="stats-card-modern-value">{activeOfs || 0}</h3>
                                                        </div>
                                                        <p className="stats-card-modern-sub">Active Ofs price books</p>
                                                    </div>
                                                </div>
                                                <div className="stats-card-modern-progress">
                                                    <div className="stats-card-modern-progress-bar" style={{
                                                        width: activeData > 0 ? `${(activeOfs / activeData) * 100}%` : '0%'
                                                    }}></div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Table Section */}
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-0">
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <Table hover className="mb-0" style={{ minWidth: '1100px' }}>
                                    <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                        <tr>
                                            <th className="py-3 px-3" style={{ minWidth: '130px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <span className="fw-semibold">Sales</span>
                                                    <div className="position-relative">
                                                        <FiSearch className="position-absolute text-muted" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }} />
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            placeholder="Search sales..."
                                                            value={searchFilters.sales}
                                                            onChange={(e) => handleSearchChange('sales', e.target.value)}
                                                            style={{ paddingLeft: '28px', fontSize: '12px' }}
                                                            className="table-search-input"
                                                        />
                                                    </div>
                                                </div>
                                            </th>
                                            <th className="py-3" style={{ minWidth: '150px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FaBuilding size={14} className="text-muted" />
                                                        <span className="fw-semibold">Customer</span>
                                                    </div>
                                                    <div className="position-relative">
                                                        <FiSearch className="position-absolute text-muted" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }} />
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            placeholder="Search customer..."
                                                            value={searchFilters.customer}
                                                            onChange={(e) => handleSearchChange('customer', e.target.value)}
                                                            style={{ paddingLeft: '28px', fontSize: '12px' }}
                                                            className="table-search-input"
                                                        />
                                                    </div>
                                                </div>
                                            </th>
                                            <th className="py-3" style={{ minWidth: '140px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FiMapPin size={14} className="text-muted" />
                                                        <span className="fw-semibold">Region</span>
                                                    </div>
                                                    <div className="position-relative">
                                                        <FiSearch className="position-absolute text-muted" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px' }} />
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            placeholder="Search region..."
                                                            value={searchFilters.region}
                                                            onChange={(e) => handleSearchChange('region', e.target.value)}
                                                            style={{ paddingLeft: '28px', fontSize: '12px' }}
                                                            className="table-search-input"
                                                        />
                                                    </div>
                                                </div>
                                            </th>
                                            <th className="py-3" style={{ minWidth: '120px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FiCalendar size={14} className="text-muted" />
                                                        <span className="fw-semibold">Start</span>
                                                    </div>
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={searchFilters.startDate}
                                                        onChange={(e) => handleSearchChange('startDate', e.target.value)}
                                                        style={{ fontSize: '12px' }}
                                                        className="table-search-input"
                                                    />
                                                </div>
                                            </th>
                                            <th className="py-3" style={{ minWidth: '120px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <FiCalendar size={14} className="text-muted" />
                                                        <span className="fw-semibold">End</span>
                                                    </div>
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={searchFilters.endDate}
                                                        onChange={(e) => handleSearchChange('endDate', e.target.value)}
                                                        style={{ fontSize: '12px' }}
                                                        className="table-search-input"
                                                    />
                                                </div>
                                            </th>
                                            <th className="py-3" style={{ minWidth: '140px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <span className="fw-semibold">Status</span>
                                                    <Form.Select
                                                        size="sm"
                                                        value={searchFilters.status}
                                                        onChange={(e) => handleSearchChange('status', e.target.value)}
                                                        style={{ fontSize: '12px' }}
                                                        className="table-search-input"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </Form.Select>
                                                </div>
                                            </th>
                                            <th className="py-3 text-center" style={{ minWidth: '70px' }}>
                                                <div className="d-flex flex-column gap-1">
                                                    <span className="fw-semibold">Version</span>
                                                    <Form.Control
                                                        type="text"
                                                        size="sm"
                                                        placeholder="v1..."
                                                        value={searchFilters.version}
                                                        onChange={(e) => handleSearchChange('version', e.target.value)}
                                                        style={{ fontSize: '12px', textAlign: 'center' }}
                                                        className="table-search-input"
                                                    />
                                                </div>
                                            </th>
                                            <th className="py-3 text-center" style={{ minWidth: '90px', verticalAlign: 'middle' }}>
                                                <div className="d-flex flex-column gap-1" style={{ justifyContent: 'center' }}>
                                                    <span className="fw-semibold">Actions</span>
                                                    <div style={{ height: '31px' }}></div>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {priceBookList.length > 0 ? (
                                            priceBookList.map((val, i) => (
                                                <tr key={i} className="align-middle">
                                                    <td className="py-3 px-3">
                                                        <span
                                                            className="d-inline-block w-100"
                                                            style={{
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                color: '#006FC9',
                                                                wordBreak: 'break-word',
                                                                lineHeight: '1.4'
                                                            }}
                                                        >
                                                            {val.CreatedBy}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className="d-inline-block w-100"
                                                            style={{
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                color: '#474747',
                                                                wordBreak: 'break-word',
                                                                lineHeight: '1.4'
                                                            }}
                                                        >
                                                            {val.CustomerName}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className="d-inline-block w-100"
                                                            style={{
                                                                fontSize: '13px',
                                                                fontWeight: '500',
                                                                color: '#474747',
                                                                wordBreak: 'break-word',
                                                                lineHeight: '1.4'
                                                            }}
                                                        >
                                                            {val.RegionName}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                                                            {formatDate(val.StartDate)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                                                            {formatDate(val.EndDate)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                            <label className="price-switch mb-0">
                                                                <input
                                                                    disabled={!val.IsActive}
                                                                    type="checkbox"
                                                                    checked={val.IsActive}
                                                                    onChange={(e) => handleStatusUpdate(e.target.checked, val)}
                                                                />
                                                                <span className="price-slider"></span>
                                                            </label>
                                                            <StatusBadge isActive={val.IsActive} />
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="px-2 py-1" style={{
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            color: '#474747',
                                                            border: '1px solid #E3E6E9',
                                                            borderRadius: '4px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            v{val.Version}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => navigate(`/pricebook/${val.OrderId}`)}
                                                                className="d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    padding: '0',
                                                                    borderRadius: '6px'
                                                                }}
                                                                title="View"
                                                            >
                                                                <FiEye size={16} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center py-5">
                                                    <div className="text-muted">
                                                        <FaTag size={48} className="mb-3 opacity-25" />
                                                        <p className="mb-0">No price books found</p>
                                                        <small>Try adjusting your filters</small>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 10 }}>
                                        <tr>
                                            <td colSpan={8} className="py-3 px-3 border-top bg-white">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-muted small">Show</span>
                                                        <Dropdown>
                                                            <Dropdown.Toggle
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                className="d-flex align-items-center gap-1"
                                                            >
                                                                {itemsPerPage}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                {[5, 10, 25, 50, 100].map(num => (
                                                                    <Dropdown.Item
                                                                        key={num}
                                                                        onClick={() => setItemsPerPage(num)}
                                                                        active={itemsPerPage === num}
                                                                    >
                                                                        {num}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                        <span className="text-muted small">per page</span>
                                                        <span className="text-muted small ms-2">
                                                            Showing {priceBookList.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} -{' '}
                                                            {Math.min(currentPage * itemsPerPage, totalData || 0)} of {totalData || 0}
                                                        </span>
                                                    </div>
                                                    <CustomPagination
                                                        currentPage={currentPage}
                                                        totalPages={totalPages}
                                                        setCurrentPage={setCurrentPage}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            </div>

            {/* Filter Offcanvas */}
            <Offcanvas show={show} onHide={handleClose} placement="end" className="filter-offcanvas">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>
                        <div className="d-flex align-items-center gap-2">
                            <FiFilter />
                            <span>Filter Price Books</span>
                            {filterCount > 0 && (
                                <Badge bg="primary" pill>{filterCount}</Badge>
                            )}
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="filter-fields">
                        <div className="form-group mb-3">
                            <label className="fw-semibold mb-2">Region</label>
                            <SearchableDropdown
                                options={regionOptions}
                                onSelect={(e) => handleFilterSelect(e, 'region')}
                                placeholder="Select region..."
                                selectedValue={selectedRegion?.label || ''}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-semibold mb-2">Status</label>
                            <SearchableDropdown
                                showAll={true}
                                options={statusOptions}
                                onSelect={(e) => handleFilterSelect(e, 'status')}
                                placeholder="Select status..."
                                selectedValue={isActive?.value === null ? 'All' : isActive?.label}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-semibold mb-2">Start Date</label>
                            <input
                                value={startDate}
                                onChange={(e) => handleDateChange(e, 'start')}
                                type="date"
                                className="form-control"
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-semibold mb-2">End Date</label>
                            <input
                                value={endDate}
                                onChange={(e) => handleDateChange(e, 'end')}
                                type="date"
                                className="form-control"
                            />
                        </div>

                        <div className="mt-4 pt-3 border-top">
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" className="flex-1" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button variant="primary" className="flex-1" onClick={handleApplyFilters}>
                                    Apply Filters
                                </Button>
                            </div>
                            <div className="mt-2">
                                <Button variant="link" className="text-danger text-decoration-none p-0" onClick={handleReset}>
                                    Reset All Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Delete Modal */}
            <Modal className="delete-modal-body" show={showDeleteModal} onHide={handleCloseDelete} centered>
                <Modal.Header className="border-0" closeButton>
                    <Modal.Title className="text-danger">
                        <FiTrash2 className="me-2" /> Delete Price Book
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-3">
                        <div className="mb-4">
                            <img src={warningIcon} alt="Warning" style={{ width: '64px' }} />
                        </div>
                        <h4 className="mb-3">Are you sure?</h4>
                        <p className="text-muted">This action cannot be undone. This will permanently delete the price book.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-center gap-2">
                    <Button variant="outline-secondary" onClick={handleCloseDelete}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Yes, Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Status Update Modal */}
            <Modal className="delete-modal-body" show={showStatusModal} onHide={handleCloseStatus} centered>
                <Modal.Header className="border-0" closeButton>
                    <Modal.Title>Update Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-3">
                        <div className="mb-4">
                            <img src={deleteIconModal} alt="Warning" style={{ width: '64px' }} />
                        </div>
                        <h4 className="mb-3">Confirm Status Change</h4>
                        <p className="text-muted">Are you sure you want to update the status?</p>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-center gap-2">
                    <Button variant="outline-secondary" onClick={handleCloseStatus}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={updateStatus}>
                        Yes, Update
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default PricebookList;